<template>
	<div class="mg-slide" :class="{ compact: compact }">
		<div v-if="imageUri" class="slide-image-container">
			<video
				v-if="isVideo"
				ref="videoElement"
				:src="imageUri"
				muted
				:loop="videoLoop"
				playsinline
				class="slide-image"
				:class="`${variant}-variant`"
			></video>
			<img
				v-else
				:src="imageUri"
				:alt="title"
				class="slide-image"
				:class="`${variant}-variant`"
			/>
		</div>

		<div class="slide-content">
			<h2 v-if="title" class="slide-title">{{ title }}</h2>
			<p v-if="isTruelayer" class="open-banking-label"><img :src="require('@/assets/figureofeight.png')" class="open-banking-icon" /> Connect with OPEN BANKING</p>
			<p v-if="description" class="slide-description">{{ description }}</p>
			<div v-if="!compact" class="slide-sub-description-container">
				<div v-if="subDescription" class="slide-sub-description">
					{{ subDescription }}
				</div>
			</div>
		</div>
	</div>
</template>

<script>
export default {
	name: 'MGSheetSlide',
	props: {
		imageUri: {
			type: String,
			default: null
		},
		imageHeight: {
			type: String,
			default: '220'
		},
		videoLoop: {
			type: Boolean,
			default: false
		},
		title: {
			type: String,
			default: null
		},
		description: {
			type: String,
			default: null
		},
		subDescription: {
			type: String,
			default: null
		},
		variant: {
			type: String,
			default: 'sheet'
		},
		compact: {
			type: Boolean,
			default: false
		},
		actionUri: {
			type: String,
			default: null
		}
	},
	computed: {
		isTruelayer() {
			return this.actionUri && this.actionUri.toLowerCase().includes('truelayer');
		},
		isVideo() {
			if (!this.imageUri) return false;
			const videoExtensions = ['.mp4', '.webm', '.mov', '.avi'];
			return videoExtensions.some(ext =>
				this.imageUri.toLowerCase().endsWith(ext)
			);
		}
	},
	methods: {
		playVideo() {
			const video = this.$refs.videoElement;
			if (video) {
				const playPromise = video.play();
				if (playPromise !== undefined) {
					playPromise.catch(error => {
						// Handle autoplay interruption errors
						// This can occur when browsers pause video-only background media to save power
						// or when autoplay policies prevent playback
						if (error.name === 'AbortError') {
							console.debug('Video autoplay was interrupted by the browser for power saving');
						} else if (error.name === 'NotAllowedError') {
							console.debug('Video autoplay was blocked by browser autoplay policy');
						}
					});
				}
			}
		}
	},
	watch: {
		imageUri() {
			this.$nextTick(() => {
				this.playVideo();
			});
		}
	},
	mounted() {
		this.$nextTick(() => {
			this.playVideo();
		});
	}
}
</script>

<style scoped>
.mg-slide {
	display: flex;
	flex-direction: column;
	height: 100%;
	min-height: 0;
}

.slide-image-container {
	flex: 0 1 auto;
	min-height: 0;
	width: 100%;
	margin-bottom: 24px;
	margin-top: 8px;
	display: flex;
	justify-content: center;
}

/* Variant-specific image constraints */
.slide-image-container:has(.sheet-variant) {
	max-height: 50%;
}

.slide-image-container:has(.story-variant) {
	max-height: 65%;
}

.slide-image {
	width: 335px;
	height: v-bind("imageHeight + 'px'");
	border-radius: 8px;
	object-fit: cover;
	flex-shrink: 0;
}

.slide-content {
	flex: 1 1 auto;
	min-height: 0;
	display: flex;
	flex-direction: column;
	gap: 12px;
	align-items: center;
	text-align: center;
	width: 100%;
	/* padding-top: 20px; */
}

.slide-title {
	font-size: 18px;
	font-weight: bold;
	color: var(--text-color);
}

.open-banking-label {
	font-size: 11px;
	margin: 0;
	opacity: 0.7;
	margin-top: -17px;
	margin-bottom: -12px;
}

.open-banking-icon {
	height: 8px;
	vertical-align: middle;
	margin-right: 4px;
}

.slide-description {
	flex: 0 0 auto;
	font-size: 13px;
	max-width: 100%;
	min-height: 60px;
}

.sheet-variant.slide-description {
	min-height: 50px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.slide-sub-description-container {
	flex: 0 0 auto;
	margin-top: 0;
	min-height: 48px;
	display: flex;
	align-items: center;
	justify-content: center;
}

.slide-sub-description {
	background: linear-gradient(90deg, #E1C6C8 0%, #FCE5D4 100%);
	border-radius: 8px;
	font-size: 13px;
	color: var(--text-color);
	padding: 4px;
	gap: 8px;
}

/* Small screens */
@media (max-height: 700px) {
	.slide-image-container:has(.sheet-variant) {
		max-height: 45%;
	}

	.sheet-variant {
		width: 200px;
		height: 130px;
	}

	.story-variant {
		width: 200px;
		height: 220px;
	}

	.slide-content {
		gap: 8px;
	}

	.slide-image-container {
		margin-bottom: 12px;
		margin-top: 4px;
	}
}

/* Compact mode */
.mg-slide.compact .slide-image-container {
	max-height: none;
	flex: 0 0 auto;
}

.mg-slide.compact .slide-content {
	flex: 0 0 auto;
	min-height: auto;
}

.mg-slide.compact .slide-description {
	min-height: auto;
}
</style>
